import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useRive, Layout, Fit, Alignment, EventType, RuntimeLoader } from '@rive-app/react-canvas';

import { buildViewModelTextValues, buildTextRunValues, buildSeasonStatusColors } from './utils/wrappedTextRuns';

// Configure Rive to use self-hosted WASM file (avoids CSP issues on staging/production)
RuntimeLoader.setWasmUrl('/rive.wasm');

import { WrappedData } from '@/api/wrapped';

interface RiveWrappedProps {
  data: WrappedData;
  onComplete: () => void;
  onShare: (imageDataUrl?: string) => void;
}

// Helper to get emoji image based on items junked
function getAdaptiveEmojiUrl(totalItems: number): string {
  if (totalItems > 100) return '/emojis/trophy.png';
  if (totalItems >= 51) return '/emojis/muscle.png';
  if (totalItems >= 21) return '/emojis/broom.png';
  if (totalItems >= 6) return '/emojis/target.png';
  return '/emojis/seedling.png';
}

export function RiveWrapped({ data, onComplete, onShare }: RiveWrappedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasCompletedRef = useRef(false);
  const isMountedRef = useRef(true);

  // Track current screen state
  const [currentScreen, setCurrentScreen] = useState(1);
  const currentScreenRef = useRef(1); // Ref to access current screen in callbacks without stale closures
  
  // Overlay state
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayOpacity, setOverlayOpacity] = useState(0);

  const touchStartXRef = useRef(0);
  const lastScrollTimeRef = useRef(0);
  const lastNavTimeRef = useRef(0); // Debounce navigation from internal Rive buttons
  const isProgrammaticSwipeRef = useRef(false); // Flag to ignore touch events during simulated swipes

  // Ref to access simulateSwipe from state change handler (defined later)
  const simulateSwipeRef = useRef<(direction: 'left' | 'right') => void>(() => {});

  // Update ref whenever state changes
  useEffect(() => {
    currentScreenRef.current = currentScreen;
    
    // Manage overlay fade in/out
    if (currentScreen === 2) {
      setShowOverlay(true);
      // Small delay to allow render before transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setOverlayOpacity(1));
      });
    } else {
      setOverlayOpacity(0);
      const timer = setTimeout(() => setShowOverlay(false), 500); // Match transition duration
      return () => clearTimeout(timer);
    }
  }, [currentScreen]);

  // Track when rive is fully loaded
  const [isRiveReady, setIsRiveReady] = useState(false);

  // Store data in ref for asset loader access
  const dataRef = useRef(data);
  dataRef.current = data;

  // Store image assets found during loading so we can manually swap them if cdnUrl fails
  const imageAssetsRef = useRef<Record<string, { asset: any, url: string }>>({});

  // Memoize Rive options to prevent re-initialization
  const riveLayout = useMemo(() => new Layout({
    fit: Fit.Contain,
    alignment: Alignment.Center,
  }), []);

  const handleRiveLoad = useCallback(() => {
    setIsRiveReady(true);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const assetLoader = useCallback((asset: any, _bytes: any) => {
    // Handle Fonts - Redirect ALL fonts to the one we have to ensure text renders
    if (asset.isFont) {
      // Point to the OTF file we have for any requested font
      // This ensures text appears even if the specific font (e.g. Outfit, Nova Square) is missing
      asset.cdnUrl = '/fonts/hyperspaceracecapsule-heavy.otf'; 
      return false; // Let Rive load from the new URL
    }

    // Handle Images (Badges)
    if (asset.isImage) {
      const assetName = asset.name;

      // Helper to normalize Rive asset names (e.g. "TheStrategicStarter" -> "strategic-starter")
      const normalizeAssetName = (name: string) => {
        let clean = name.replace(/^The/, ''); // Remove "The" prefix
        clean = clean.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase(); // Camel to kebab
        return clean;
      };

      const normalizedName = normalizeAssetName(assetName);
      
      // Robust matching helper: simplify string to just lowercase letters/numbers
      const simpleAssetName = assetName.toLowerCase().replace(/[^a-z0-9]/g, '');

      let badgeUrl = '';

      // Map personality types (underscore format) to PNG file names in public folder
      const personalityBadgePngMap: Record<string, string> = {
        'strategic_starter': '/StrategicStarter.png',
        'day_one_degen': '/DayOneDegen.png',
        'seasonal_sprinter': '/SeasonalSprinter.png',
        'chest_chaser': '/ChestChaser.png',
        'lucky_draw_believer': '/LuckyDrawBeliever.png',
        'steady_climber': '/TheSteadyClimber.png',
        'seasonal_whale': '/TheSeasonalWhale.png',
        'completionist': '/TheCompletionist.png',
      };

      // Check if this is a personality/junking style badge request
      // If asset name contains "personality", "junking", or "style", use the user's actual personality type
      const isPersonalityRequest = assetName.toLowerCase().includes('personality') ||
                                   assetName.toLowerCase().includes('junking') ||
                                   assetName.toLowerCase().includes('style');

      // Check if asset name matches any known personality type (ignoring case/symbols)
      // e.g. "TheStrategicStarter" contains "strategicstarter" (derived from "strategic_starter")
      const matchedPersonalityKey = Object.keys(personalityBadgePngMap).find(key => {
        const simpleKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
        return simpleAssetName.includes(simpleKey);
      });

      if (isPersonalityRequest || matchedPersonalityKey) {
        // Get user's personality type and look up the PNG file
        // Default to strategic_starter if personality type is missing or invalid
        const userPersonality = dataRef.current?.personalityType || 'strategic_starter';
        badgeUrl = personalityBadgePngMap[userPersonality] || personalityBadgePngMap['strategic_starter'];
      }
      // Handle Founder/Genesis Badge
      else if (assetName.includes('Badge') || assetName.includes('Founder') || assetName.includes('Genesis')) {
        badgeUrl = '/badges/days-badge.svg';
      }
      // Handle Adaptive Emoji based on user's items junked
      else if (assetName.toLowerCase().includes('emoji') || assetName.toLowerCase().includes('adaptive')) {
        const emojiUrl = getAdaptiveEmojiUrl(dataRef.current?.totalItemsJunked || 0);
        const absoluteUrl = new URL(emojiUrl, window.location.origin).href;
        asset.cdnUrl = absoluteUrl;
        imageAssetsRef.current[assetName] = { asset, url: absoluteUrl };
        return false;
      }
      // Handle RNG/Faith emoji (100)
      else if (assetName.toLowerCase().includes('faith') || assetName.toLowerCase().includes('rng')) {
        const emojiUrl = '/emojis/trophy.png'; // Fallback for now since we don't have 100.png
        const absoluteUrl = new URL(emojiUrl, window.location.origin).href;
        asset.cdnUrl = absoluteUrl;
        imageAssetsRef.current[assetName] = { asset, url: absoluteUrl };
        return false;
      }
      // Handle specific emoji names directly
      else if (['seedling', 'target', 'broom', 'muscle', 'trophy'].includes(normalizedName)) {
        const emojiUrl = `/emojis/${normalizedName}.png`;
        const absoluteUrl = new URL(emojiUrl, window.location.origin).href;
        asset.cdnUrl = absoluteUrl;
        imageAssetsRef.current[assetName] = { asset, url: absoluteUrl };
        return false;
      }

      if (badgeUrl) {
        // Construct absolute URL for cdnUrl - sometimes Rive needs this
        const absoluteUrl = new URL(badgeUrl, window.location.origin).href;
        asset.cdnUrl = absoluteUrl;
        // Store for manual swapping in useEffect where we HAVE the rive instance
        imageAssetsRef.current[assetName] = { asset, url: absoluteUrl };
        return false; // Return false to let Rive try to load from cdnUrl first
      }
    }

    return false;
  }, []);

  // Initialize Rive with single persistent instance
  const { rive, RiveComponent } = useRive({
    src: '/wrapped_25.riv',
    artboard: 'Wrapped_25',
    stateMachines: 'MainFlow',
    autoplay: true,
    autoBind: true,
    layout: riveLayout,
    assetLoader: assetLoader,
    onLoad: handleRiveLoad,
  });

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Manually swap images if they were captured during load
  useEffect(() => {
    if (!rive || !isRiveReady) return;

    const swapImages = async () => {
      const assetsToSwap = Object.entries(imageAssetsRef.current);
      if (assetsToSwap.length === 0) return;

      for (const [, { asset, url }] of assetsToSwap) {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          const buffer = await blob.arrayBuffer();
          const bytes = new Uint8Array(buffer);

          // Try to find decodeImage method
          let decoded = null;
          // @ts-ignore
          if (typeof rive.decodeImage === 'function') {
            // @ts-ignore
            decoded = await rive.decodeImage(bytes);
          } else if (typeof (rive as any).layout?.runtime?.decodeImage === 'function') {
            // @ts-ignore
            decoded = await (rive as any).layout.runtime.decodeImage(bytes);
            // @ts-ignore
          } else if (typeof asset.decode === 'function') {
            // @ts-ignore
            decoded = await asset.decode(bytes);
            // @ts-ignore
          } else if (rive.runtime && typeof rive.runtime.decodeImage === 'function') {
            // @ts-ignore
            decoded = await rive.runtime.decodeImage(bytes);
          }

          if (decoded) {
            asset.setRenderImage(decoded);
            decoded.unref();
          }
        } catch {
          // Silently ignore image swap failures
        }
      }
    };

    swapImages();
  }, [rive, isRiveReady]);

  // Set text values once on load
  useEffect(() => {
    if (!rive || !isRiveReady) return;

    // @ts-ignore - viewModelInstance exists at runtime
    const vmi = rive.viewModelInstance;
    if (!vmi) return;

    const textValues = buildViewModelTextValues(data);

    // First, set numbered fields (Txt 1 through Txt 42)
    for (let i = 1; i <= 42; i++) {
      const txtFormat = `Txt ${i}`;
      const numFormat = i.toString();
      const value = textValues[txtFormat] || textValues[numFormat];
      if (value === '' || value === undefined) continue;

      try {
        const stringProperty = vmi.string(txtFormat) || vmi.string(numFormat);
        if (stringProperty) {
          stringProperty.value = value;
        }
      } catch {
        // Silently ignore - field may not exist
      }
    }

    // Also try setting non-numbered text fields (like streak messages, etc.)
    // These may have custom names in the Rive file
    Object.entries(textValues).forEach(([key, value]) => {
      // Skip numbered fields (already handled above)
      if (/^(Txt )?\d+$/.test(key)) return;
      if (value === '' || value === undefined) return;

      try {
        const stringProperty = vmi.string(key);
        if (stringProperty) {
          stringProperty.value = value;
        }
      } catch {
        // Silently ignore - field may not exist
      }
    });

    // Set season status colors (Color S1 - Color S5)
    // Colors: Participant = #50E4F9, Skipped = red, In Progress = yellow
    const statusColors = buildSeasonStatusColors(data.seasonDetails);
    Object.entries(statusColors).forEach(([colorName, color]) => {
      if (!color) return; // Skip if no color override needed

      try {
        const colorProperty = vmi.color(colorName);
        if (colorProperty) {
          // Rive expects color as 0xAARRGGBB or individual r,g,b,a properties
          // Try setting via individual components first
          // @ts-ignore - runtime property check
          if (typeof colorProperty.r !== 'undefined') {
            // @ts-ignore - runtime properties
            colorProperty.r = color.r;
            // @ts-ignore
            colorProperty.g = color.g;
            // @ts-ignore
            colorProperty.b = color.b;
            // @ts-ignore
            colorProperty.a = color.a;
          } else {
            // Try setting as a packed color value (0xAARRGGBB)
            const packedColor =
              ((Math.round(color.a * 255) & 0xFF) << 24) |
              ((Math.round(color.r * 255) & 0xFF) << 16) |
              ((Math.round(color.g * 255) & 0xFF) << 8) |
              (Math.round(color.b * 255) & 0xFF);
            // @ts-ignore
            colorProperty.value = packedColor;
          }
        }
      } catch {
        // Silently ignore - color property may not exist
      }
    });

    // Fallback: Also try to set Legacy Text Runs (named text runs)
    const legacyValues = buildTextRunValues(data);

    Object.values(legacyValues).forEach((screenGroup) => {
      Object.entries(screenGroup).forEach(([runName, value]) => {
        try {
          // @ts-ignore
          if (rive && typeof rive.setTextRunValue === 'function') {
            // @ts-ignore
            rive.setTextRunValue(runName, value);
          }
        } catch {
          // Ignore errors for missing runs
        }
      });
    });
  }, [rive, isRiveReady, data]);

  // Helper function to capture canvas and share
  const captureAndShare = useCallback(() => {
    const canvas = containerRef.current?.querySelector('canvas');
    if (!canvas) {
      onShare();
      return;
    }

    // Hide navigation buttons before capture by setting Button_visuality to true
    // @ts-ignore - viewModelInstance exists at runtime
    const vmi = rive?.viewModelInstance;
    let buttonVisibilityProp: any = null;

    if (vmi) {
      try {
        buttonVisibilityProp = vmi.boolean('Button_visuality');
        if (buttonVisibilityProp) {
          buttonVisibilityProp.value = true;
        }
      } catch {
        // Silently ignore if property doesn't exist
      }
    }

    // Wait for Rive to render without buttons, then capture
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          // Add padding to the top of the captured image
          const topPadding = 20;
          const paddedCanvas = document.createElement('canvas');
          paddedCanvas.width = canvas.width;
          paddedCanvas.height = canvas.height + topPadding;

          const ctx = paddedCanvas.getContext('2d');
          if (ctx) {
            // Fill with the background color
            ctx.fillStyle = '#0a0a14';
            ctx.fillRect(0, 0, paddedCanvas.width, paddedCanvas.height);
            // Draw the original canvas with offset
            ctx.drawImage(canvas, 0, topPadding);
          }

          const imageDataUrl = paddedCanvas.toDataURL('image/png');
          onShare(imageDataUrl);
        } catch {
          // If canvas capture fails (e.g., tainted canvas), share without image
          onShare();
        }

        // Reset button visibility after capture
        if (buttonVisibilityProp) {
          try {
            buttonVisibilityProp.value = false;
          } catch {
            // Silently ignore
          }
        }
      });
    });
  }, [onShare, rive]);

  // Handle Rive events - including internal navigation buttons
  useEffect(() => {
    if (!rive) return;

    const handleRiveEvent = (riveEvent: any) => {
      if (!isMountedRef.current) return;
      const eventName = riveEvent?.data?.name || riveEvent?.name;

      const isShareEvent = [
        'Share_Clicked', 'ShareClicked', 'Click_Share', 'ClickShare',
        'Tweet_Clicked', 'TweetClicked', 'Click_Tweet', 'ClickTweet',
        'Share', 'Tweet'
      ].includes(eventName);

      if (isShareEvent) {
        captureAndShare();
      }

      // Handle internal Next/Forward buttons from Rive
      const isNextEvent = [
        'Click_Next', 'ClickNext', 'Next', 'Button_Next', 'Btn_Next',
        'Arrow_Right', 'Right_Clicked', 'Next_Clicked', 'Forward',
        'Click_Forward', 'Btn_Forward', 'ClickN', 'Click N'
      ].includes(eventName);

      if (isNextEvent) {
        const nextScreen = Math.min(currentScreen + 1, 15);
        setCurrentScreen(nextScreen);
        if (nextScreen >= 11 && !hasCompletedRef.current) {
          hasCompletedRef.current = true;
          onComplete();
        }
      }

      // Handle internal Prev/Back buttons from Rive
      const isPrevEvent = [
        'Click_Prev', 'ClickPrev', 'Prev', 'Previous', 'Button_Prev', 'Btn_Prev',
        'Arrow_Left', 'Left_Clicked', 'Prev_Clicked', 'Back',
        'Click_Back', 'Btn_Back', 'ClickP', 'Click P'
      ].includes(eventName);

      if (isPrevEvent) {
        setCurrentScreen(Math.max(currentScreen - 1, 1));
      }
    };

    rive.on(EventType.RiveEvent, handleRiveEvent);

    return () => {
      rive.off(EventType.RiveEvent, handleRiveEvent);
    };
  }, [rive, captureAndShare, currentScreen, onComplete]);

  // Listen for Rive state changes to sync our screen tracking with internal navigation
  useEffect(() => {
    if (!rive) return;

    const handleStateChange = (event: any) => {
      if (!isMountedRef.current) return;

      // Extract state names from the event
      const stateNames = event?.data || [];

      // Check for Navigation triggers via State Changes (ClickN / ClickP)
      // Debounce to prevent double navigation from state change cascades
      const now = Date.now();
      if (now - lastNavTimeRef.current < 400) {
        return;
      }

      const hasNextState = stateNames.some((name: string) =>
        ['ClickN', 'Click N', 'Click_N', 'ClickNext', 'Next'].includes(name)
      );

      if (hasNextState) {
        lastNavTimeRef.current = now; // Mark navigation time

        // Use ref to get latest screen without stale closure
        const current = currentScreenRef.current;
        const nextScreen = Math.min(current + 1, 15);

        // Delay swipe simulation to let Rive finish processing its click event
        setTimeout(() => {
          if (simulateSwipeRef.current) {
            simulateSwipeRef.current('left');
          }
          // Also set the boolean state to ensure logic updates
          navigateToScreen(nextScreen);
          
          // Re-enable MainFlow playback after swipe to ensure animations start
          if (rive) {
             // @ts-ignore
             if (typeof rive.play === 'function') rive.play('MainFlow');
          }
        }, 50);

        // Update screen tracking
        setCurrentScreen(nextScreen);
        if (nextScreen >= 11 && !hasCompletedRef.current) {
          hasCompletedRef.current = true;
          onComplete();
        }
        return; // Don't process prev if we just processed next
      }

      const hasPrevState = stateNames.some((name: string) =>
        ['ClickP', 'Click P', 'Click_P', 'ClickPrev', 'Prev'].includes(name)
      );

      if (hasPrevState) {
        lastNavTimeRef.current = now; // Mark navigation time

        const current = currentScreenRef.current;
        const prevScreen = Math.max(current - 1, 1);

        // Delay swipe simulation to let Rive finish processing its click event
        setTimeout(() => {
          if (simulateSwipeRef.current) {
            simulateSwipeRef.current('right');
          }
          // Also set the boolean state to ensure logic updates
          navigateToScreen(prevScreen);
          
          // Re-enable MainFlow playback after swipe to ensure animations start
          if (rive) {
             // @ts-ignore
             if (typeof rive.play === 'function') rive.play('MainFlow');
          }
        }, 50);

        // Update screen tracking
        setCurrentScreen(prevScreen);
        return;
      }

      // Check for Share triggers via State Changes (Case-insensitive)
      const hasShareState = stateNames.some((name: string) => {
        const lower = name.toLowerCase();
        // Specifically look for Share_Clicked as requested by user
        return lower.includes('share') || lower.includes('tweet') || lower.includes('x_click') || name === 'Share_Clicked';
      });

      if (hasShareState) {
        captureAndShare();
      }

      // Check if we transitioned to a screen state (S1, S2, etc.)
      for (const stateName of stateNames) {
        const match = stateName?.match?.(/^S\s?(\d+)$/i);
        if (match) {
          const screenNum = parseInt(match[1], 10);
          if (screenNum >= 1 && screenNum <= 11) {
            // Update state if it differs
            if (screenNum !== currentScreenRef.current) {
               setCurrentScreen(screenNum);
            }
            if (screenNum >= 11 && !hasCompletedRef.current) {
              hasCompletedRef.current = true;
              onComplete();
            }
          }
        }
      }
    };

    // @ts-ignore - StateChange event type
    rive.on(EventType.StateChange, handleStateChange);

    return () => {
      // @ts-ignore
      rive.off(EventType.StateChange, handleStateChange);
    };
  }, [rive, onComplete]);

  // Navigate to screen using viewModelInstance for data-bound properties
  const navigateToScreen = useCallback((screenNumber: number) => {
    if (screenNumber < 1 || screenNumber > 15) return;
    if (!rive) return;

    let handled = false;

    // 1. Try ViewModel (Data Binding) - This is the likely correct path given autoBind: true
    // @ts-ignore
    const vmi = rive.viewModelInstance;
    if (vmi) {
      const propNameWithSpace = `S ${screenNumber}`;
      const propNameNoSpace = `S${screenNumber}`;

      try {
        // First, reset ALL other screens to false to ensure clean transition
        for (let i = 1; i <= 15; i++) {
           if (i === screenNumber) continue;
           const otherSpace = `S ${i}`;
           const otherNoSpace = `S${i}`;
           try {
             const otherBool = vmi.boolean(otherSpace) || vmi.boolean(otherNoSpace);
             if (otherBool && otherBool.value === true) {
                otherBool.value = false;
             }
           } catch { /* ignore */ }
        }

        // Also check if we are stuck in a "Click" state and reset those too
        const resetTriggers = ['ClickN', 'ClickP', 'Click N', 'Click P'];
        for (const trigger of resetTriggers) {
           try {
             const t = vmi.boolean(trigger);
             if (t && t.value === true) {
                t.value = false;
             }
           } catch { /* ignore */ }
        }

        const boolProp = vmi.boolean(propNameWithSpace) || vmi.boolean(propNameNoSpace);
        if (boolProp) {
          boolProp.value = true;
          handled = true;
        }
      } catch (e) {
        console.error('[RiveWrapped] Error setting VMI boolean:', e);
      }
    }

    // 2. Fallback to State Machine Inputs (Legacy/Manual)
    if (!handled) {
      // @ts-ignore - access artboard directly
      const artboard = rive.artboard;

      if (artboard && typeof artboard.inputByPath === 'function') {
        const pathFormats = [
          `S ${screenNumber}`,
          `S${screenNumber}`,
          `MainFlow/S ${screenNumber}`,
          `MainFlow/S${screenNumber}`,
        ];

        for (const path of pathFormats) {
          try {
            const input = artboard.inputByPath(path);
            if (input) {
              // @ts-ignore
              if (typeof input.value !== 'undefined') {
                input.value = true;
                // Also reset previous screens to false just in case
                if (screenNumber > 1) {
                  // This loop helps reset state if the state machine logic is additive
                  for (let i = 1; i < screenNumber; i++) {
                     // Try to find previous inputs and turn them off?
                     // Usually not needed if Rive is set up correctly, but helpful for debugging
                  }
                }
                break;
              }
              // @ts-ignore
              if (typeof input.fire === 'function') {
                input.fire();
                break;
              }
            }
          } catch {
            // Try next format
          }
        }
      }
    }

      // 3. Fallback: Direct Animation Playback (Bypass State Machine)
      // If we have timelines named "S2", "S3", etc., play them directly.
      if (!handled && rive.animationNames.includes(`S${screenNumber}`)) {
         // Stop other animations to avoid conflict
         rive.stop(rive.animationNames.filter(n => n.startsWith('S') && n !== `S${screenNumber}`));
         rive.play(`S${screenNumber}`);

         // Also ensure MainFlow is running, as it might handle nested animations
         // @ts-ignore
         if (typeof rive.play === 'function') rive.play('MainFlow');

         handled = true;
      }

    // 4. Try scrubbing the MainTimeline to the target screen position
    // 11 screens = positions at 0%, ~9%, ~18%, etc. of the timeline
    if (!handled && rive.animationNames.includes('MainTimeline')) {
      try {
        // @ts-ignore - accessing internal animation API
        const artboard = rive.artboard;
        if (artboard) {
          // @ts-ignore
          const animation = artboard.animationByName?.('MainTimeline') || artboard.animation?.('MainTimeline');
          if (animation) {
            const duration = animation.duration || 1;
            const targetTime = ((screenNumber - 1) / 10) * duration;
            // @ts-ignore
            if (typeof animation.time !== 'undefined') {
              animation.time = targetTime;
              handled = true;
            }
          }
        }
      } catch {
        // Silently ignore timeline scrub failures
      }
    }

    // 5. Ultimate Fallback: Simulate Swiping (Force Rive to scroll)
    // This is useful if the Rive file relies on scroll position or listeners we can't trigger
    // but responds to drag gestures. We can't easily simulate drag *events* to Rive's canvas
    // from here, but we can verify if we are missing any "Scroll" or "Drag" inputs.

    // If we have "MainFlow" running, sometimes re-triggering it helps restart animations
    // that might have paused during the transition
    if (rive && !handled) {
       // @ts-ignore
       if (typeof rive.play === 'function') rive.play('MainFlow');
    }

    setCurrentScreen(screenNumber);
    
    // Check if this is the final screen (screen 11)
    if (screenNumber >= 11 && !hasCompletedRef.current) {
      hasCompletedRef.current = true;
      onComplete();
    }
  }, [currentScreen, onComplete, rive]);

  // Handle swipe to navigate screens (left/right)
  const handleScroll = useCallback((direction: 'left' | 'right') => {
    const now = Date.now();
    // Debounce to prevent rapid scrolling
    if (now - lastScrollTimeRef.current < 500) return;
    lastScrollTimeRef.current = now;

    let nextScreen = currentScreen;

    // Swipe left = next screen, swipe right = previous screen
    // Screen 1 is intro, screens 2-11 are content screens
    if (direction === 'left') {
      nextScreen = Math.min(currentScreen + 1, 15); // Increased cap to 15
    } else {
      nextScreen = Math.max(currentScreen - 1, 1);
    }

    navigateToScreen(nextScreen);
  }, [currentScreen, navigateToScreen]);

  // Touch/swipe handlers for horizontal swipes
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Skip if this is from a programmatic swipe simulation
    if (isProgrammaticSwipeRef.current) {
      return;
    }
    touchStartXRef.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    // Skip if this is from a programmatic swipe simulation
    if (isProgrammaticSwipeRef.current) {
      return;
    }

    const touchEndX = e.changedTouches[0].clientX;
    const deltaX = touchStartXRef.current - touchEndX;

    // Require minimum swipe distance of 50px
    if (Math.abs(deltaX) > 50) {
      if (deltaX > 0) {
        // Swipe left = go to next screen
        handleScroll('left');
      } else {
        // Swipe right = go to previous screen
        handleScroll('right');
      }
    }
  }, [handleScroll]);

  // Mouse wheel handler for desktop (horizontal scroll or shift+scroll)
  const handleWheel = useCallback((e: React.WheelEvent) => {
    // For regular vertical scroll (deltaY), we also want to trigger navigation
    // Use deltaX for horizontal scroll, or deltaY for vertical scroll
    const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    
    if (delta > 0) {
      handleScroll('left'); // Scroll down/right -> next
    } else {
      handleScroll('right'); // Scroll up/left -> previous
    }
  }, [handleScroll]);

  // Simulate a swipe gesture by calling Rive's internal pointer methods directly
  // This bypasses DOM event filtering and triggers the state machine transitions
  const simulateSwipe = useCallback((direction: 'left' | 'right') => {
    // Set flag to prevent our touch handlers from double-processing
    isProgrammaticSwipeRef.current = true;
    // Clear the flag after the simulation completes
    setTimeout(() => {
      isProgrammaticSwipeRef.current = false;
    }, 300);

    if (!rive) return;

    const container = containerRef.current;
    if (!container) return;

    const canvas = container.querySelector('canvas');
    if (!canvas) return;

    try {
      // Get artboard dimensions for coordinate transformation
      // @ts-ignore - accessing internal Rive properties
      const artboard = rive.artboard;
      if (!artboard) return;

      // Use artboard coordinates directly (Rive expects artboard space)
      const artboardWidth = artboard.bounds?.maxX || 430;
      const artboardHeight = artboard.bounds?.maxY || 932;
      const centerY = artboardHeight / 2;

      // Calculate swipe coordinates in artboard space
      const swipeDistance = artboardWidth * 0.3; // 30% of artboard width
      const startX = direction === 'left'
        ? artboardWidth * 0.7  // Start from right side
        : artboardWidth * 0.3; // Start from left side
      const endX = direction === 'left'
        ? startX - swipeDistance  // Move left
        : startX + swipeDistance; // Move right

      // Get state machine instances and call pointer methods directly
      // @ts-ignore - accessing internal Rive properties
      const stateMachineInstances = rive.stateMachineInstances || rive._stateMachineInstances;

      if (stateMachineInstances && stateMachineInstances.length > 0) {

        for (const sm of stateMachineInstances) {
          // Call pointer methods on the state machine instance
          if (typeof sm.pointerDown === 'function') {
            sm.pointerDown(startX, centerY);

            // Simulate drag movement with multiple intermediate points
            const steps = 5;
            for (let i = 1; i <= steps; i++) {
              const progress = i / steps;
              const currentX = startX + (endX - startX) * progress;
              setTimeout(() => {
                if (typeof sm.pointerMove === 'function') {
                  sm.pointerMove(currentX, centerY);
                }
                // Final pointerUp on the last step
                if (i === steps && typeof sm.pointerUp === 'function') {
                  setTimeout(() => {
                    sm.pointerUp(endX, centerY);
                  }, 10);
                }
              }, i * 20);
            }
          }
        }
      } else {
         // Try using mouse events instead - Rive handles mousedown/mousemove/mouseup
         // Use PointerEvent for better modern browser support if MouseEvent fails or is insufficient
         const rect = canvas.getBoundingClientRect();
         const startClientX = rect.left + (direction === 'left' ? rect.width * 0.7 : rect.width * 0.3);
         const endClientX = rect.left + (direction === 'left' ? rect.width * 0.3 : rect.width * 0.7);
         const clientY = rect.top + rect.height / 2;

         // Common pointer event options
         const pointerOpts = {
           bubbles: true,
           cancelable: true,
           view: window,
           pointerId: 1,
           pointerType: 'touch',
           isPrimary: true,
           pressure: 0.5,
           clientX: startClientX,
           clientY: clientY,
           button: 0,
           buttons: 1,
         };

         // Dispatch pointerdown
         canvas.dispatchEvent(new PointerEvent('pointerdown', pointerOpts));
         // Also dispatch mousedown for fallback compatibility
         canvas.dispatchEvent(new MouseEvent('mousedown', { ...pointerOpts, clientX: startClientX, clientY: clientY }));

         // Simulate drag with multiple pointermove events
         const steps = 10;
         const stepDelay = 15;
         for (let i = 1; i <= steps; i++) {
           setTimeout(() => {
             const progress = i / steps;
             const currentX = startClientX + (endClientX - startClientX) * progress;
             
             canvas.dispatchEvent(new PointerEvent('pointermove', { 
               ...pointerOpts, 
               clientX: currentX, 
               clientY: clientY 
             }));
             canvas.dispatchEvent(new MouseEvent('mousemove', { 
               ...pointerOpts, 
               clientX: currentX, 
               clientY: clientY 
             }));

             // On last step, dispatch pointerup/mouseup
             if (i === steps) {
               setTimeout(() => {
                 canvas.dispatchEvent(new PointerEvent('pointerup', { 
                   ...pointerOpts, 
                   clientX: endClientX, 
                   clientY: clientY,
                   buttons: 0,
                   pressure: 0
                 }));
                 canvas.dispatchEvent(new MouseEvent('mouseup', { 
                   ...pointerOpts, 
                   clientX: endClientX, 
                   clientY: clientY,
                   buttons: 0
                 }));
               }, 10);
             }
           }, i * stepDelay);
         }
       }
     } catch (error) {
      console.error('[RiveWrapped] simulateSwipe error:', error);
    }
  }, [rive]);


  // Trigger Rive Click_Next input for navigation
  const triggerClickNext = useCallback(() => {
    if (!rive) return;

    let handled = false;

    // Method 1: Use stateMachineInputs API (most reliable)
    try {
      const inputs = rive.stateMachineInputs('MainFlow');
      if (inputs) {
        // Try all possible next trigger/boolean names
        const nextNames = ['Click_Next', 'ClickNext', 'Next', 'Button_Next', 'Btn_Next', 'Forward'];
        const clickNextInput = inputs.find((input: any) => nextNames.includes(input.name));
        
        if (clickNextInput) {
          // Check if it's a trigger (has fire method)
          // @ts-ignore
          if (typeof clickNextInput.fire === 'function') {
            // @ts-ignore
            clickNextInput.fire();
            handled = true;
          } else {
            // It's a boolean, set to true then false
            // @ts-ignore
            clickNextInput.value = true;
            // @ts-ignore
            setTimeout(() => { clickNextInput.value = false; }, 100);
            handled = true;
          }
        }
      }
    } catch {
      // Silently ignore
    }

    // Method 2: Try viewModelInstance boolean
    if (!handled) {
      // @ts-ignore - viewModelInstance exists at runtime
      const vmi = rive.viewModelInstance;
      if (vmi) {
        const nextNames = [
          'Click_Next', 'ClickNext', 'Next', 'Button_Next', 'Btn_Next', 'Forward',
          'ClickN', 'Click N', 'Click_N'
        ];
        for (const name of nextNames) {
          try {
            const clickNext = vmi.boolean(name);
            if (clickNext) {
              clickNext.value = true;
              setTimeout(() => { clickNext.value = false; }, 100);
              handled = true;
              break;
            }
          } catch { continue; }
        }
      }
    }

    // Method 3: Ultimate fallback - use navigateToScreen
    if (!handled) {
      handleScroll('left');
    }
  }, [rive, handleScroll]);

  // Trigger Rive Click_Prev input for navigation
  const triggerClickPrev = useCallback(() => {
    if (!rive) return;

    let handled = false;

    // Method 1: Use stateMachineInputs API (most reliable)
    try {
      const inputs = rive.stateMachineInputs('MainFlow');
      if (inputs) {
        // Try all possible prev trigger/boolean names
        const prevNames = ['Click_Prev', 'ClickPrev', 'Prev', 'Previous', 'Button_Prev', 'Btn_Prev', 'Back'];
        const clickPrevInput = inputs.find((input: any) => prevNames.includes(input.name));
        
        if (clickPrevInput) {
          // Check if it's a trigger (has fire method)
          // @ts-ignore
          if (typeof clickPrevInput.fire === 'function') {
            // @ts-ignore
            clickPrevInput.fire();
            handled = true;
          } else {
            // It's a boolean, set to true then false
            // @ts-ignore
            clickPrevInput.value = true;
            // @ts-ignore
            setTimeout(() => { clickPrevInput.value = false; }, 100);
            handled = true;
          }
        }
      }
    } catch {
      // Silently ignore
    }

    // Method 2: Try viewModelInstance boolean
    if (!handled) {
      // @ts-ignore - viewModelInstance exists at runtime
      const vmi = rive.viewModelInstance;
      if (vmi) {
        const prevNames = [
          'Click_Prev', 'ClickPrev', 'Prev', 'Previous', 'Button_Prev', 'Btn_Prev', 'Back',
          'ClickP', 'Click P', 'Click_P'
        ];
        for (const name of prevNames) {
          try {
            const clickPrev = vmi.boolean(name);
            if (clickPrev) {
              clickPrev.value = true;
              setTimeout(() => { clickPrev.value = false; }, 100);
              handled = true;
              break;
            }
          } catch { continue; }
        }
      }
    }

    // Method 3: Ultimate fallback - use navigateToScreen
    if (!handled) {
      handleScroll('right');
    }
  }, [rive, handleScroll]);

  // Desktop button click handlers - simulate swipe gestures to trigger Rive's built-in listeners
    const handleNextClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      // Simulate swipe left to go to next screen (same as real swipe)
      simulateSwipe('left');
      // Also update our internal screen tracking
      const nextScreen = Math.min(currentScreen + 1, 15); // Increased cap to 15
      setCurrentScreen(nextScreen);
      if (nextScreen >= 11 && !hasCompletedRef.current) {
      hasCompletedRef.current = true;
      onComplete();
    }
  }, [simulateSwipe, currentScreen, onComplete]);

  const handlePrevClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // Simulate swipe right to go to previous screen (same as real swipe)
    simulateSwipe('right');
    // Also update our internal screen tracking
    const prevScreen = Math.max(currentScreen - 1, 1);
    setCurrentScreen(prevScreen);
  }, [simulateSwipe, currentScreen]);

  // Update ref for keyboard navigation and state change handler
  simulateSwipeRef.current = simulateSwipe;

  // Keyboard navigation - uses swipe simulation like button clicks
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        simulateSwipeRef.current('right'); // Arrow left = go to previous (swipe right)
      } else if (e.key === 'ArrowRight') {
        simulateSwipeRef.current('left'); // Arrow right = go to next (swipe left)
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div
      className="fixed inset-0 w-full h-full overflow-hidden flex items-center justify-center"
      style={{
        backgroundColor: '#0a0a14',
        touchAction: 'none', // Prevent browser scroll/gestures on the whole page
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
    >
      {/* Container for the Rive animation - constrained on desktop, full on mobile */}
      <div
        ref={containerRef}
        className="w-full h-full md:max-w-[430px] md:max-h-[932px] md:h-[90vh] md:rounded-2xl md:overflow-hidden md:shadow-2xl relative"
        style={{
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
        }}
      >
        <RiveComponent
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
          }}
        />

        {/* Custom Header Overlay for Screen 2 */}
        {showOverlay && (
          <div 
            className="absolute top-0 left-0 w-full p-8 flex justify-between items-start pointer-events-none z-20 transition-opacity duration-500 ease-in-out"
            style={{ opacity: overlayOpacity }}
          >
            {/* Left: Logo + wrapped text */}
            <div className="flex flex-col items-end">
              {/* Using standard img tag for simplicity within this component */}
              <img src="/logo.svg" alt="Junk.Fun" className="h-6 w-auto mb-[-4px]" />
              <span 
                className="text-white font-light tracking-wide"
                style={{ fontFamily: 'Outfit, sans-serif', fontSize: '16px' }}
              >
                wrapped
              </span>
            </div>

            {/* Right: '25 Year */}
            <div style={{
              color: '#FFF',
              textAlign: 'right',
              fontFamily: 'Outfit, sans-serif',
              fontSize: '32px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: 'normal',
              marginTop: '-8px' // Slight optical adjustment to align baseline with logo
            }}>
              '25
            </div>
          </div>
        )}
      </div>

      {/* External Share Button (Positioned at the very bottom of the viewport) */}
      {currentScreen >= 11 && (
        <div className="absolute bottom-2 left-0 w-full flex justify-center px-8 z-[50]">
          <button
            onClick={(e) => {
              e.stopPropagation();
              captureAndShare();
            }}
            className="w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-cyan-400 text-black font-bold rounded-xl hover:from-cyan-400 hover:to-cyan-300 border border-cyan-300/30 transition-all transform active:scale-95 shadow-[0_4px_20px_rgba(34,211,238,0.4)] pointer-events-auto cursor-pointer text-base"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Share on X
          </button>
        </div>
      )}
    </div>
  );
}
